/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */
import { g17ecoApi, transformResponse } from '@api/g17ecoApi';
import { ExternalEntity, IntegrationConnectionInfo, IntegrationProvider, IntegrationProviderExtended, ProviderSetupInfo, SetupData, SubConnection } from '@g17eco/types/app-integration';
import type { DataTransferResponse } from '@g17eco/types/data-transfer';
import { ValueData } from '../types/universalTrackerValue';


interface PartnerGet {
  initiativeId: string,
  code: string
}

interface InitiativeAppIntegrationsResponse {
  allProviders: IntegrationProvider[];
  usedProviders: IntegrationProviderExtended[];
}

interface SubConnectionCreate {
  initiativeId: string;
  code: string;
  targetInitiativeId: string;
  externalId: string;
  facilityName: string;
}

interface SubConnectionUpdate {
  initiativeId: string;
  code: string;
  taskId: string;
  targetInitiativeId?: string;
  externalId?: string;
  facilityName?: string;
}

interface SubConnectionDelete {
  initiativeId: string;
  code: string;
  taskId: string;
}

interface ConnectionDataUpdate {
  initiativeId: string;
  code: string;
  externalId: string;
}

interface DataTransferQuery {
  initiativeId: string;
  code: string;
  childInitiativeId: string;
  startDate: string;
  endDate: string;
}

export const integrationsApi = g17ecoApi
  .enhanceEndpoints({
    addTagTypes: ['integrations', 'integration', 'integration-setup-info', 'sub-connections', 'external-entities'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      listInitiativeAppIntegrations: builder.query<InitiativeAppIntegrationsResponse, string>({
        transformResponse,
        query: (initiativeId) => ({
          url: `/initiatives/${initiativeId}/integrations`,
          method: 'get',
        }),
        providesTags: ['integrations']
      }),
      getInitiativeAppIntegration: builder.query<ProviderSetupInfo, PartnerGet>({
        transformResponse,
        query: ({ initiativeId, code }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}`,
          method: 'get',
        }),
        providesTags: (_result, _error, arg) => [{ type: 'integration-setup-info', id: arg.code }],
      }),
      getUsedInitiativeAppIntegration: builder.query<
        IntegrationConnectionInfo<{ answers: { valueData: ValueData }[] }>,
        PartnerGet
      >({
        transformResponse,
        query: ({ initiativeId, code }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/used`,
          method: 'get',
        }),
        providesTags: (_result, _error, arg) => [{ type: 'integration', id: arg.code }],
      }),
      createInitiativeAppIntegration: builder.mutation<ProviderSetupInfo, SetupData>({
        transformResponse,
        query: ({ initiativeId, code, ...data }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}`,
          method: 'post',
          data: data,
        }),
        invalidatesTags: (_result, _error, agr) => ['integrations', { type: 'integration-setup-info', id: agr.code }],
      }),
      listExternalEntities: builder.query<ExternalEntity[], PartnerGet>({
        transformResponse,
        query: ({ initiativeId, code }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/external-entities`,
          method: 'get',
        }),
        providesTags: (_result, _error, arg) => [{ type: 'external-entities', id: arg.code }],
      }),
      listSubConnections: builder.query<SubConnection[], PartnerGet>({
        transformResponse,
        query: ({ initiativeId, code }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/sub-connections`,
          method: 'get',
        }),
        providesTags: (_result, _error, arg) => [{ type: 'sub-connections', id: arg.code }],
      }),
      createSubConnection: builder.mutation<SubConnection, SubConnectionCreate>({
        transformResponse,
        query: ({ initiativeId, code, targetInitiativeId, externalId, facilityName }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/sub-connections`,
          method: 'post',
          data: { initiativeId: targetInitiativeId, externalId, facilityName },
        }),
        invalidatesTags: (_result, _error, arg) => [{ type: 'sub-connections', id: arg.code }],
      }),
      updateSubConnection: builder.mutation<SubConnection, SubConnectionUpdate>({
        transformResponse,
        query: ({ initiativeId, code, taskId, targetInitiativeId, externalId, facilityName }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/sub-connections/${taskId}`,
          method: 'patch',
          data: {
            ...(targetInitiativeId ? { initiativeId: targetInitiativeId } : {}),
            ...(externalId ? { externalId } : {}),
            ...(facilityName ? { facilityName } : {}),
          },
        }),
        invalidatesTags: (_result, _error, arg) => [{ type: 'sub-connections', id: arg.code }],
      }),
      deleteSubConnection: builder.mutation<{ success: boolean }, SubConnectionDelete>({
        transformResponse,
        query: ({ initiativeId, code, taskId }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/sub-connections/${taskId}`,
          method: 'delete',
        }),
        invalidatesTags: (_result, _error, arg) => [{ type: 'sub-connections', id: arg.code }],
      }),
      updateConnectionExternalId: builder.mutation<unknown, ConnectionDataUpdate>({
        transformResponse,
        query: ({ initiativeId, code, externalId }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/connection-data`,
          method: 'patch',
          data: { externalId },
        }),
        invalidatesTags: (_result, _error, arg) => [{ type: 'integration', id: arg.code }],
      }),
      getDataTransferComparison: builder.query<DataTransferResponse, DataTransferQuery>({
        transformResponse,
        query: ({ initiativeId, code, childInitiativeId, startDate, endDate }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/data-transfer`,
          method: 'get',
          params: { childInitiativeId, startDate, endDate },
        }),
      }),
      getDataTransferExport: builder.query<Blob, DataTransferQuery>({
        query: ({ initiativeId, code, childInitiativeId, startDate, endDate }) => ({
          url: `/initiatives/${initiativeId}/integrations/${code}/data-transfer/export`,
          method: 'get',
          params: { childInitiativeId, startDate, endDate },
          responseType: 'blob',
        }),
      }),
    }),
  });

export const {
  useGetInitiativeAppIntegrationQuery,
  useListInitiativeAppIntegrationsQuery,
  useGetUsedInitiativeAppIntegrationQuery,
  useCreateInitiativeAppIntegrationMutation,
  useListExternalEntitiesQuery,
  useListSubConnectionsQuery,
  useCreateSubConnectionMutation,
  useUpdateSubConnectionMutation,
  useDeleteSubConnectionMutation,
  useUpdateConnectionExternalIdMutation,
  useGetDataTransferComparisonQuery,
  useLazyGetDataTransferExportQuery,
} = integrationsApi;
